import { useState } from "react";
import { ActiveErrorMessage, DevicesButton, usePropsFor } from "@azure/communication-react";
import { getDevicePermissionState } from "../../utils/Utils";
import { useCallAdapter } from "../../provider/CallAdapterProvider";
import { DeviceCheckOptions } from "../../interfaces/DeviceCheckOptions";
import { devicePermissionSelector, localVideoSelector, selectPcpCallState } from "../../store/selectors";
import { usePcpLiveLessonContext } from "../../store/PcpLiveLessonStoreContext";

export const useCommonConfigurationPageHandlers = (props: {
    deviceChecks?: DeviceCheckOptions,
    latestErrors: ActiveErrorMessage[],
}) => {

    const {
        deviceChecks,
        latestErrors,
    } = props;

    const adapter = useCallAdapter();
    const pcpCallState = usePcpLiveLessonContext(selectPcpCallState);
    const deviceState = adapter.getState().devices;
    const options = usePropsFor(DevicesButton);
    const { video: cameraPermissionGranted, audio: microphonePermissionGranted } = usePcpLiveLessonContext(devicePermissionSelector);
    // use permission API to get video and audio permission state
    const [videoState, setVideoState] = useState<PermissionState | 'unsupported' | undefined>(undefined);
    const [audioState, setAudioState] = useState<PermissionState | 'unsupported' | undefined>(undefined);

    const isCameraOn = usePcpLiveLessonContext(localVideoSelector).isAvailable;

    getDevicePermissionState(setVideoState, setAudioState);

    const role = adapter.getState().call?.role;


    const permissionsState: {
        camera: PermissionState;
        microphone: PermissionState;
    } = {
        // fall back to using cameraPermissionGranted and microphonePermissionGranted if permission API is not supported
        camera:
            videoState && videoState !== 'unsupported'
                ? cameraPermissionGranted !== false
                    ? videoState
                    : 'denied'
                : cameraPermissionGranted !== false
                    ? cameraPermissionGranted
                        ? 'granted'
                        : 'prompt'
                    : 'denied',
        microphone:
            audioState && audioState !== 'unsupported'
                ? microphonePermissionGranted !== false
                    ? audioState
                    : 'denied'
                : microphonePermissionGranted !== false
                    ? microphonePermissionGranted
                        ? 'granted'
                        : 'prompt'
                    : 'denied'
    };

    // const networkErrors = errorBarProps.activeErrorMessages.filter((message) => message.type === 'callNetworkQualityLow');
    const [isPermissionsModalDismissed, setIsPermissionsModalDismissed] = useState(true);

    // When permission API is not available, we want to show screen saying checking for access (disappears on its own)
    // then based on permission setting, we show permission denied or nothing
    const [minimumFallbackTimerElapsed, setMinimumFallbackTimerElapsed] = useState(false);
    setTimeout(() => {
        setMinimumFallbackTimerElapsed(true);
    }, 2000);
    const forceShowingCheckPermissions = !minimumFallbackTimerElapsed;


    // const filteredErrorBarProps = useMemo(
    //     () => ({
    //         ...errorBarProps,
    //         activeErrorMessages: filteredLatestErrors
    //     }),
    //     [errorBarProps, filteredLatestErrors]
    // );

    let disableStartCallButton = !microphonePermissionGranted || deviceState.microphones?.length === 0 || pcpCallState !== 'started';
    let filteredLatestErrors: ActiveErrorMessage[] = latestErrors;

    // TODO: move this logic to the error bar selector once role is plumbed from the headless SDK
    if (role !== 'Consumer') {
        filteredLatestErrors = filteredLatestErrors.filter(
            (e) => e.type !== 'callCameraAccessDenied' && e.type !== 'callCameraAccessDeniedSafari'
        );
    }

    if (role === 'Consumer') {
        // If user's role permissions do not allow access to the microphone button then DO NOT disable the start call button
        // because microphone device permission is not needed for the user's role
        disableStartCallButton = false;
    }

    // Overrides role permissions if CallCompositeOptions deviceChecks are set
    if (deviceChecks) {
        if (
            ['doNotPrompt', 'optional'].includes(deviceChecks.camera) &&
            ['doNotPrompt', 'optional'].includes(deviceChecks.microphone)
        ) {
            disableStartCallButton = false;
        } else if (deviceChecks.camera === 'required') {
            disableStartCallButton = !cameraPermissionGranted || deviceState.cameras?.length === 0;
        }
    }

    return {
        options,
        cameraPermissionGranted,
        microphonePermissionGranted,
        role,
        isCameraOn,
        videoState,
        audioState,
        permissionsState,
        disableStartCallButton,
        setIsPermissionsModalDismissed,
    };
};
